import { __awaiter } from "tslib";
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { ReducerType } from './types';
import { MISSING_CUSTOMER } from '@open-tender/types';
import { currentLocalDate, isoToDate, makeSimpleCart, rehydrateCart } from '@open-tender/utils';
import { resetOrder, selectTimezone, setCart, setMenuVars } from './order';
import { checkAuth, makeCartData, selectToken } from './customer';
const initialState = {
    cart: [],
    cartGuest: null,
    cartGuests: [],
    cartId: null,
    cartOwner: null,
    closed: false,
    cutoffAt: null,
    guestCount: null,
    guestLimit: null,
    isCartOwner: false,
    requestedAt: null,
    revenueCenterId: null,
    serviceType: null,
    spendingLimit: null,
    token: null,
    error: null,
    loading: 'idle'
};
export var GroupOrderActionType;
(function (GroupOrderActionType) {
    GroupOrderActionType["JoinGroupOrder"] = "groupOrder/joinGroupOrder";
    GroupOrderActionType["FetchGroupOrder"] = "groupOrder/fetchGroupOrder";
    GroupOrderActionType["UpdateGroupOrder"] = "groupOrder/updateGroupOrder";
    GroupOrderActionType["ReloadGuestOrder"] = "groupOrder/reloadGuestOrder";
    GroupOrderActionType["AddCustomerGroupOrder"] = "customer/addCustomerGroupOrder";
})(GroupOrderActionType || (GroupOrderActionType = {}));
export const makeCartPayload = (response, cartGuestId) => {
    const { customer = null, closed, cart_id: cartId, token, cart, revenue_center_id: revenueCenterId, service_type: serviceType, requested_at: requestedAt, cutoff_at: cutoffAt, spending_limit: spendingLimit, guest_limit: guestLimit, guest_count: guestCount, cart_guests: cartGuests } = response;
    const filteredCart = cartGuestId
        ? cart.filter(i => i.cart_guest_id === cartGuestId)
        : cart;
    return {
        cartId,
        token,
        revenueCenterId,
        serviceType,
        requestedAt,
        cutoffAt,
        guestLimit,
        guestCount,
        spendingLimit,
        closed,
        cartOwner: customer,
        cart: filteredCart,
        cartGuests: cartGuests || []
    };
};
export const joinGroupOrder = createAsyncThunk(GroupOrderActionType.JoinGroupOrder, (requestData, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    const { api } = getState().config;
    if (!api)
        return null;
    try {
        const data = {
            cart_id: requestData.cart_id,
            first_name: requestData.first_name,
            last_name: requestData.last_name
        };
        const response = yield api.postCartGuest(data);
        const { cart_guest_id: cartGuestId, first_name: firstName, last_name: lastName } = response;
        const { revenueCenter } = getState().order;
        dispatch(resetOrder());
        const { serviceType, requestedAt } = getState().groupOrder;
        dispatch(setMenuVars({ revenueCenter, serviceType, requestedAt }));
        dispatch(setCart([]));
        const payload = { cartGuestId, firstName, lastName };
        return payload;
    }
    catch (err) {
        return rejectWithValue(err);
    }
}));
export const fetchGroupOrder = createAsyncThunk(GroupOrderActionType.FetchGroupOrder, (id, { getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    const { api } = getState().config;
    if (!api)
        return;
    try {
        const response = yield api.getCart(id);
        const { cartGuest } = getState().groupOrder;
        const cartGuestId = cartGuest ? cartGuest.cartGuestId : null;
        const payload = makeCartPayload(response, cartGuestId);
        return payload;
    }
    catch (err) {
        return rejectWithValue(err);
    }
}));
export const updateGroupOrder = createAsyncThunk(GroupOrderActionType.UpdateGroupOrder, (_, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    const { api } = getState().config;
    if (!api)
        return;
    const { cartId, cartGuest } = getState().groupOrder;
    const orderCart = getState().order.cart;
    if (!orderCart || !cartGuest)
        return;
    try {
        const data = {
            cart: makeSimpleCart(orderCart),
            cart_guest_id: cartGuest.cartGuestId
        };
        const response = yield api.putCart(cartId, data);
        const payload = makeCartPayload(response, cartGuest.cartGuestId);
        return payload;
    }
    catch (err) {
        yield dispatch(fetchGroupOrder(cartId));
        return rejectWithValue(err);
    }
}));
export const reloadGuestOrder = createAsyncThunk(GroupOrderActionType.ReloadGuestOrder, (_, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    const { api } = getState().config;
    if (!api)
        return;
    try {
        const { revenueCenterId, serviceType } = getState().groupOrder;
        const { cart: items } = getState().groupOrder;
        const menuItems = yield api.getMenuItems(revenueCenterId, serviceType);
        const { cart } = rehydrateCart(menuItems, items);
        dispatch(setCart(cart));
        return;
    }
    catch (err) {
        return rejectWithValue(err);
    }
}));
export const addCustomerGroupOrder = createAsyncThunk(GroupOrderActionType.AddCustomerGroupOrder, ({ spendingLimit, callback }, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        const cartData = makeCartData(getState().order, spendingLimit);
        const response = yield api.postCustomerGroupOrder(token, cartData);
        const customer = getState().customer.account.profile;
        const payload = Object.assign(Object.assign({}, makeCartPayload(response)), { isCartOwner: true, cartOwner: customer });
        if (callback)
            callback();
        return payload;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
const groupOrderSlice = createSlice({
    name: ReducerType.GroupOrder,
    initialState,
    reducers: {
        resetGroupOrder: () => initialState,
        adjustGroupOrder: (state, action) => {
            return Object.assign(Object.assign({}, state), action.payload);
        }
    },
    extraReducers: builder => {
        builder
            .addCase(joinGroupOrder.fulfilled, (state, action) => {
            state.loading = 'idle';
            state.error = null;
            state.cartGuest = action.payload;
        })
            .addCase(joinGroupOrder.pending, state => {
            state.loading = 'pending';
        })
            .addCase(joinGroupOrder.rejected, (state, action) => {
            state.loading = 'idle';
            state.error = action.payload;
        })
            .addCase(fetchGroupOrder.fulfilled, (state, action) => {
            return Object.assign(Object.assign(Object.assign({}, state), { loading: 'idle', error: null }), action.payload);
        })
            .addCase(fetchGroupOrder.pending, state => {
            state.loading = 'pending';
        })
            .addCase(fetchGroupOrder.rejected, (state, action) => {
            state.loading = 'idle';
            state.error = action.payload;
        })
            .addCase(updateGroupOrder.fulfilled, (state, action) => {
            return Object.assign(Object.assign(Object.assign({}, state), { loading: 'idle', error: null }), action.payload);
        })
            .addCase(updateGroupOrder.pending, state => {
            state.loading = 'pending';
        })
            .addCase(updateGroupOrder.rejected, (state, action) => {
            state.loading = 'idle';
            state.error = action.payload;
        })
            .addCase(reloadGuestOrder.fulfilled, state => {
            state.loading = 'idle';
        })
            .addCase(reloadGuestOrder.pending, state => {
            state.loading = 'pending';
        })
            .addCase(reloadGuestOrder.rejected, (state, action) => {
            state.loading = 'idle';
            state.error = action.payload;
        })
            .addCase(addCustomerGroupOrder.fulfilled, (state, action) => {
            return Object.assign(Object.assign(Object.assign({}, state), action.payload), { loading: 'idle', error: null });
        })
            .addCase(addCustomerGroupOrder.pending, state => {
            state.loading = 'pending';
        })
            .addCase(addCustomerGroupOrder.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        });
    }
});
export const { resetGroupOrder, adjustGroupOrder } = groupOrderSlice.actions;
export const selectGroupOrder = (state) => state.groupOrder;
export const selectGroupOrderToken = (state) => state.groupOrder.token;
export const selectSpendingLimit = createSelector((state) => {
    const { cartGuest, spendingLimit } = state.groupOrder;
    return { cartGuest, spendingLimit };
}, ({ cartGuest, spendingLimit }) => {
    return cartGuest && spendingLimit ? parseFloat(spendingLimit) : null;
});
export const selectGroupOrderClosed = createSelector((state) => {
    const { cartGuest, cutoffAt, closed } = state.groupOrder;
    const tz = selectTimezone(state);
    return { cartGuest, cutoffAt, closed, tz };
}, ({ cartGuest, cutoffAt, closed, tz }) => {
    const cutoffDate = cutoffAt ? isoToDate(cutoffAt, tz) : null;
    const pastCutoff = cutoffDate ? currentLocalDate(tz) > cutoffDate : false;
    return cartGuest && (closed || pastCutoff);
});
export const selectGroupOrderTests = createSelector((state) => {
    const { closed, cutoffAt, guestLimit, guestCount } = state.groupOrder;
    const tz = selectTimezone(state);
    return { closed, cutoffAt, guestLimit, guestCount, tz };
}, ({ closed, cutoffAt, guestLimit, guestCount, tz }) => {
    const cutoffDate = cutoffAt ? isoToDate(cutoffAt, tz) : null;
    const pastCutoff = cutoffDate ? new Date() > cutoffDate : false;
    const spotsRemaining = guestLimit ? guestLimit - (guestCount !== null && guestCount !== void 0 ? guestCount : 0) : null;
    const atCapacity = spotsRemaining !== null && spotsRemaining <= 0;
    return { closed, pastCutoff, atCapacity };
});
export const selectGroupOrderPrepTimes = createSelector((state) => {
    const { revenueCenter, serviceType } = state.order;
    return { revenueCenter, serviceType };
}, ({ revenueCenter, serviceType }) => {
    if (!revenueCenter || !serviceType)
        return {};
    const { wait_times, group_ordering } = revenueCenter;
    const { prep_time, lead_time: leadTime } = group_ordering || {};
    const st = serviceType === 'WALKIN' ? 'PICKUP' : serviceType;
    const waitTime = wait_times && wait_times[st] ? wait_times[st] : 0;
    const prepTime = (waitTime !== null && waitTime !== void 0 ? waitTime : 0) + (prep_time !== null && prep_time !== void 0 ? prep_time : 0);
    return { prepTime, leadTime };
});
export const groupOrderReducer = groupOrderSlice.reducer;
